/**
 * Report output layer: turns form data into downloadable/previewable report
 * files in whichever format the user's facility uses (PDF or PNG).
 * The format is an admin-controlled per-facility setting delivered in
 * facilityInfo.reportFormat; technologists cannot override it.
 *
 * Everything here runs in the browser — no PHI is sent to the server.
 */

import { generatePDFInBrowser, downloadBlob, previewBlob } from './index';
import { buildReportBaseName, buildPngFileName, getReportSections } from './reportNaming';

const DOWNLOAD_GAP_MS = 400; // spacing between multi-file downloads so browsers don't drop them

export function getReportFormat(facilityInfo) {
  return facilityInfo && facilityInfo.reportFormat === 'png' ? 'png' : 'pdf';
}

/**
 * @returns {Promise<Array<{ blob: Blob, fileName: string }>>}
 */
export async function generateReportFiles(examType, formData, facilityInfo) {
  const baseName = buildReportBaseName(formData, examType);

  if (getReportFormat(facilityInfo) !== 'png') {
    const blob = await generatePDFInBrowser(examType, formData, facilityInfo);
    return [{ blob, fileName: `${baseName}.pdf` }];
  }

  const { pdfBlobToPngBlobs } = await import('./rasterize');
  const files = [];
  for (const section of getReportSections(examType, formData)) {
    const pdfBlob = await generatePDFInBrowser(examType, section.data, facilityInfo);
    const pages = await pdfBlobToPngBlobs(pdfBlob);
    pages.forEach((blob, i) => {
      files.push({ blob, fileName: buildPngFileName(baseName, section.label, i, pages.length) });
    });
  }
  return files;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Download every file, one after another. */
export async function saveReportFiles(files) {
  for (let i = 0; i < files.length; i++) {
    if (i > 0) await wait(DOWNLOAD_GAP_MS);
    downloadBlob(files[i].blob, files[i].fileName);
  }
}

/** Success message shown after saving. */
export function describeSavedReport(files) {
  if (files.length === 1) {
    const isPng = files[0].fileName.endsWith('.png');
    return `✓ ${isPng ? 'PNG image' : 'PDF'} generated and downloaded: ${files[0].fileName}`;
  }
  return `✓ ${files.length} PNG images generated and downloaded: ${files.map((f) => f.fileName).join(', ')}`;
}

/**
 * PNG rasterisation can take several seconds, longer than browsers allow
 * between a click and window.open(). Call this synchronously at the start of a
 * preview click to reserve the tab, then pass it to showReportPreview().
 * Returns null for PDF facilities (previewBlob handles those).
 */
export function reservePreviewWindow(facilityInfo) {
  if (getReportFormat(facilityInfo) !== 'png') return null;
  const win = window.open('', '_blank');
  if (win) {
    win.document.title = 'Generating report…';
    win.document.body.style.cssText = 'font-family:sans-serif;padding:24px';
    win.document.body.textContent = 'Generating report preview…';
  }
  return win;
}

/** Close a reserved preview tab if generation failed. */
export function abandonPreviewWindow(previewWindow) {
  if (previewWindow && !previewWindow.closed) previewWindow.close();
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read image'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Preview generated files: PDF opens as before; PNGs are stacked in one tab.
 * PNG images are embedded as data: URLs (not blob: URLs) because the reserved tab
 * starts as about:blank, and Chrome fails "Save image as" on blob: images there
 * ("Failed - Network error"). Each image also gets an explicit Save link that
 * downloads it under its proper file name.
 */
export async function showReportPreview(files, previewWindow) {
  if (!files.length) throw new Error('Nothing to preview');
  if (!files[0].fileName.endsWith('.png')) {
    return previewBlob(files[0].blob);
  }

  if (!previewWindow || previewWindow.closed) {
    throw new Error('Preview blocked by browser. Please allow pop-ups for this site and try again.');
  }

  const urls = await Promise.all(files.map((f) => blobToDataUrl(f.blob)));
  const doc = previewWindow.document;
  doc.title = 'Report preview';
  doc.body.textContent = '';
  doc.body.style.cssText = 'margin:0;padding:16px;background:#666';
  files.forEach((file, i) => {
    const figure = doc.createElement('figure');
    figure.style.cssText = 'margin:0 auto 16px;max-width:900px';
    const img = doc.createElement('img');
    img.src = urls[i];
    img.alt = file.fileName;
    img.style.cssText = 'width:100%;display:block;background:#fff';
    const caption = doc.createElement('figcaption');
    caption.style.cssText = 'color:#fff;font:12px sans-serif;margin-top:4px';
    const link = doc.createElement('a');
    link.href = urls[i];
    link.download = file.fileName;
    link.textContent = `Save ${file.fileName}`;
    link.style.cssText = 'color:#9cf';
    caption.appendChild(link);
    figure.append(img, caption);
    doc.body.appendChild(figure);
  });
  return previewWindow;
}

/** Non-fatal audit ping: examType + format only, never PHI. */
export function postReportAudit({ apiUrl, token, examType, facilityInfo }) {
  if (!apiUrl || !token) return;
  fetch(`${apiUrl}/api/audit-log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ examType, format: getReportFormat(facilityInfo) })
  }).catch(() => {}); // non-fatal
}
